// src/components/CancelTaskModal.jsx

import React, { useState, useEffect } from 'react';
import { cancelClientTask } from '../Services/masterService';
import Modal from '../AlertModal/Modal';

// Assume you have an API service function defined:
// import { cancelTaskApi } from '../services/taskService'; 

// Enum/List for 'cancelledBy' options based on your payload example
const CANCELLERS = [
    { value: 'CLIENT', label: 'Partner' },
    // { value: 'LOGISTICS', label: 'Logistics' },
];

// Initial state for the cancellation payload
const initialPayload = {
    taskId: null, // This must be passed in as a prop
    cancelReason: '',
    cancelledBy: 'CLIENT', // Default to 'OPERATION'
    userId: null, // Default user ID (adjust as needed)
};

const CancelClientTaskModal = ({
    isOpen,
    onClose,
    taskToCancel, // The full task object or just { taskId: 11, userId: 1 }
    onSuccess,
    // cancelTaskApi // Pass the API function as a prop if needed
}) => {
    // Initialize state with the required task ID and default user ID
    const [payload, setPayload] = useState({
        ...initialPayload,
        taskId: taskToCancel?.taskId || null,
        userId: taskToCancel?.userId || 1,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [userInfo, setUserInfo] = useState('unknowne')

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({});

    const showSuccess = (msg) => {
        setModalConfig({
            title: "Success!",
            message: msg,
            type: "success",
        });
        setIsModalOpen(true);
    };

    const showError = (titleMsg, message) => {
        setModalConfig({
            title: titleMsg,
            message: message,
            type: "error",
        });
        setIsModalOpen(true);
    };

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUserInfo(JSON.parse(storedUser));
        }
    }, []);

    // Update taskId and userId when the modal is opened with a new task
    React.useEffect(() => {
        if (taskToCancel) {
            setPayload(prev => ({
                ...prev,
                taskId: taskToCancel.taskId,
                userId: taskToCancel.userId || 1, // Fallback to 1 if user ID is missing
            }));
        }
    }, [taskToCancel]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setPayload(prev => ({ ...prev, [name]: value }));
    };

    const handleCancel = () => {
        // Reset state when closing
        setPayload(prev => ({
            ...initialPayload,
            taskId: taskToCancel?.taskId || null,
            userId: taskToCancel?.userId || 0,
        }));
        setError('');
        onClose();
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!payload.cancelReason.trim()) {
            showError('Sorry!', 'Cancellation Reason is required.');
            return;
        }

        if (!payload.taskId) {
            showError('Error!', ' Task ID is missing.');
            return;
        }
        setIsSubmitting(true);

        try {
            handleCancel();
            const response = await cancelClientTask(payload);
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (response.status == "CANCELLED") {
                showSuccess("Task Cancelled Successfully!")
                onSuccess(response.message);
                handleCancel();
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                showError('Sorry!', 'Failed to cancel the task.');
            }
        } catch (err) {
            showError('Sorry!', 'A network error occurred during cancellation.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75" onClick={handleCancel}>
            <div
                className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 border-t-4 border-red-600"
                onClick={e => e.stopPropagation()} // Prevent closing when clicking inside
            >
                <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center">
                    ⚠️ Confirm Task Cancellation
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-gray-700">
                        Task ID: <span className="font-semibold">{payload.taskId}</span>
                    </p>

                    {/* Cancelled By Dropdown */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cancelled By
                        </label>
                        <select
                            name="cancelledBy"
                            value={payload.cancelledBy}
                            onChange={handleChange}
                            required
                            // 🛑 ADD THE 'disabled' ATTRIBUTE HERE 🛑
                            disabled={true}

                            // 🎨 Conditional styling to visually indicate it's disabled
                            className={`w-full border border-gray-300 p-2 rounded-md transition duration-150 
            ${true
                                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                                    : 'focus:ring-green-500 focus:border-green-500'}`
                            }
                        >
                            {CANCELLERS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label} - {userInfo.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Reason Textarea */}
                    <div>
                        <label htmlFor="cancelReason" className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Cancellation
                        </label>
                        <textarea
                            id="cancelReason"
                            name="cancelReason"
                            rows="3"
                            value={payload.cancelReason}
                            onChange={handleChange}
                            required
                            className="w-full border border-gray-300 p-2 rounded-md focus:ring-red-500 focus:border-red-500"
                            placeholder="Enter a detailed reason..."
                        />
                    </div>

                    {/* Error Message */}
                    {error && (
                        <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>
                    )}

                    <div className="flex justify-end space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-150"
                        >
                            Close
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition duration-150 
                                ${isSubmitting
                                    ? 'bg-red-400 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700 text-white'}`}
                        >
                            {isSubmitting ? 'Cancelling...' : 'Confirm Cancellation'}
                        </button>
                    </div>
                </form>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
            />
        </div>
    );
};

export default CancelClientTaskModal;